import React, { useState, useEffect, useCallback } from 'react';
import { getQuestions, deleteQuestion, updateQuestion } from '../api';
import './QuestionsManager.css';

const QuestionsManager = ({ isOpen, onClose, courseId = null, courseTitle = null, quizId = null, quizTitle = null }) => {
  const [questions, setQuestions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingData, setEditingData] = useState({ body: '', options: [] });
  const [updatingId, setUpdatingId] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // If courseId is provided, use it; otherwise fall back to quizId
      const filterId = courseId || quizId;
      
      if (!filterId) {
        setError('No course or quiz selected');
        setQuestions([]);
        setTotalQuestions(0);
        setLoading(false);
        return;
      }
      
      // Fetch all pages of questions
      let allQuestions = [];
      let page = 1;
      let hasMore = true;
      let total = 0;
      
      while (hasMore) {
        // Always pass courseId if it exists - never set it to null
        const result = await getQuestions(courseId, quizId, page, 100);
        
        // Handle paginated response (object with questions array)
        if (result && typeof result === 'object' && !Array.isArray(result) && result.questions) {
          allQuestions = [...allQuestions, ...result.questions];
          total = result.total || allQuestions.length;
          const lastPage = result.lastPage || 1;
          hasMore = page < lastPage;
          page++;
          
          // Safety check: if no questions returned, we're done
          if (result.questions.length === 0) {
            hasMore = false;
          }
        } else if (Array.isArray(result)) {
          // Non-paginated response (just an array)
          allQuestions = [...allQuestions, ...result];
          total = allQuestions.length;
          
          // If we got fewer items than requested, we're done
          if (result.length < 100) {
            hasMore = false;
          } else {
            page++;
          }
        } else {
          // Unexpected format
          hasMore = false;
        }
      }

      setQuestions(allQuestions);
      setTotalQuestions(total);
      
      console.log(`QuestionsManager: Loaded ${allQuestions.length} questions (total: ${total}) for courseId: ${courseId}, quizId: ${quizId}`);
      if (allQuestions.length > 0) {
        console.log(`QuestionsManager: Sample question structure:`, allQuestions[0]);
        console.log(`QuestionsManager: First question course_id:`, allQuestions[0].course_id || allQuestions[0].test?.course_id);
      } else {
        console.warn(`QuestionsManager: No questions found! Check API response above.`);
      }
      
      // If no questions found and we have a course, show appropriate message
      if (allQuestions.length === 0 && courseId) {
        setError(null); // Don't show error, just empty state
        console.log(`No questions found for course ${courseId}`);
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError(err.message || 'Failed to load questions');
      setQuestions([]);
      setTotalQuestions(0);
    } finally {
      setLoading(false);
    }
  }, [courseId, quizId]);

  useEffect(() => {
    if (isOpen && (courseId || quizId)) {
      fetchQuestions();
    } else if (isOpen && !courseId && !quizId) {
      // Don't fetch if no course or quiz selected
      setLoading(false);
      setQuestions([]);
      setTotalQuestions(0);
    }
  }, [isOpen, courseId, quizId, fetchQuestions]);

  const handleEdit = (question) => {
    setEditingQuestion(question.id);
    setEditingData({
      body: question.body || '',
      options: question.options ? [...question.options] : []
    });
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditingData({ body: '', options: [] });
  };

  const handleUpdateOption = (optionIndex, field, value) => {
    const updatedOptions = [...editingData.options];
    if (field === 'body') {
      updatedOptions[optionIndex] = { ...updatedOptions[optionIndex], body: value };
    } else if (field === 'is_correct') {
      // Only one option can be correct, so unset others
      updatedOptions.forEach((opt, idx) => {
        opt.is_correct = idx === optionIndex;
      });
    }
    setEditingData({ ...editingData, options: updatedOptions });
  };

  const handleDeleteOption = (optionIndex) => {
    if (editingData.options.length <= 2) {
      alert('A question must have at least 2 options');
      return;
    }
    const updatedOptions = editingData.options.filter((_, idx) => idx !== optionIndex);
    setEditingData({ ...editingData, options: updatedOptions });
  };

  const handleAddOption = () => {
    const newOption = { body: '', is_correct: false };
    setEditingData({ ...editingData, options: [...editingData.options, newOption] });
  };

  const handleSaveEdit = async (questionId) => {
    try {
      setUpdatingId(questionId);
      
      // Validate
      if (!editingData.body.trim()) {
        alert('Question body cannot be empty');
        return;
      }
      if (editingData.options.length < 2) {
        alert('A question must have at least 2 options');
        return;
      }
      const correctCount = editingData.options.filter(opt => opt.is_correct).length;
      if (correctCount !== 1) {
        alert('Exactly one option must be marked as correct');
        return;
      }

      // Prepare payload
      const payload = {
        body: editingData.body.trim(),
        options: editingData.options.map(opt => ({
          body: opt.body.trim(),
          is_correct: opt.is_correct || false
        })),
        test_id: questions.find(q => q.id === questionId)?.test_id || null,
        course_id: questions.find(q => q.id === questionId)?.course_id || null,
      };

      await updateQuestion(questionId, payload);
      
      // Update local state
      const updatedQuestions = questions.map(q => 
        q.id === questionId 
          ? { ...q, body: editingData.body, options: editingData.options }
          : q
      );
      setQuestions(updatedQuestions);
      
      // Clear edit mode
      handleCancelEdit();
      
      // Show success feedback
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
      
      console.log(`Successfully updated question ${questionId}`);
    } catch (err) {
      console.error('Error updating question:', err);
      const errorMessage = err?.message || err?.error || 'Failed to update question. Please try again.';
      alert(errorMessage);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (questionId) => {
    // First click: show confirmation
    if (!showDeleteConfirm || showDeleteConfirm !== questionId) {
      setShowDeleteConfirm(questionId);
      return;
    }

    // Second click: confirm and delete
    try {
      setDeletingId(questionId);
      await deleteQuestion(questionId);
      
      // Remove the question from the list
      const updatedQuestions = questions.filter(q => q.id !== questionId);
      setQuestions(updatedQuestions);
      
      // Update total count
      setTotalQuestions(prev => Math.max(0, prev - 1));
      
      // Clear selection and confirmation
      setShowDeleteConfirm(null);
      if (selectedQuestion === questionId) {
        setSelectedQuestion(null);
      }
      if (editingQuestion === questionId) {
        handleCancelEdit();
      }
      
      // Show success feedback
      setDeleteSuccess(true);
      setTimeout(() => setDeleteSuccess(false), 3000);
      
      console.log(`Successfully deleted question ${questionId}`);
    } catch (err) {
      console.error('Error deleting question:', err);
      const errorMessage = err?.message || err?.error || 'Failed to delete question. Please try again.';
      alert(errorMessage);
      // Clear confirmation on error so user can try again
      setShowDeleteConfirm(null);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredQuestions = questions.filter(q => {
    const searchLower = searchTerm.toLowerCase();
    const body = (q.body || '').toLowerCase();
    const testTitle = (q.test?.title || '').toLowerCase();
    return body.includes(searchLower) || testTitle.includes(searchLower);
  });

  const getQuestionType = (question) => {
    if (question.type) return question.type;
    if (question.options && question.options.length > 0) return 'Multiple Choice';
    return 'Text';
  };

  const getCorrectAnswer = (question) => {
    if (!question.options || question.options.length === 0) return null;
    const correct = question.options.find(opt => opt.is_correct);
    return correct ? correct.body : null;
  };

  if (!isOpen) return null;

  return (
    <div className="questions-manager-overlay" onClick={onClose}>
      <div className="questions-manager-sidebar" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="questions-manager-header">
          <div className="questions-manager-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <path d="M12 17h.01" />
            </svg>
            <div>
              <h2>{courseTitle || quizTitle || 'Quiz Questions'}</h2>
              {(courseTitle || quizTitle) && (
                <p className="questions-subtitle">
                  {courseTitle ? 'Questions for this course\'s published quiz' : 'Questions for this quiz'}
                </p>
              )}
            </div>
            <span className="questions-count">{totalQuestions || questions.length}</span>
          </div>
          <button className="questions-manager-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Success Messages */}
        {deleteSuccess && (
          <div className="questions-success-message">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Question deleted successfully
          </div>
        )}
        {updateSuccess && (
          <div className="questions-success-message">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Question updated successfully
          </div>
        )}

        {/* Search Bar */}
        <div className="questions-manager-search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="questions-search-input"
          />
          {searchTerm && (
            <button
              className="questions-search-clear"
              onClick={() => setSearchTerm('')}
              aria-label="Clear search"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="questions-manager-content">
          {loading ? (
            <div className="questions-loading">
              <div className="questions-spinner"></div>
              <p>Loading questions...</p>
            </div>
          ) : error ? (
            <div className="questions-error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p>{error}</p>
              <button className="questions-retry-btn" onClick={fetchQuestions}>
                Retry
              </button>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="questions-empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <path d="M12 17h.01" />
              </svg>
              <p>
                {searchTerm 
                  ? 'No questions match your search' 
                  : courseId 
                    ? 'No published quiz or questions found for this course'
                    : 'No questions found'}
              </p>
            </div>
          ) : (
            <div className="questions-list">
              {filteredQuestions.map((question, index) => (
                <div
                  key={question.id}
                  className={`question-card ${selectedQuestion === question.id ? 'selected' : ''} ${deletingId === question.id ? 'deleting' : ''}`}
                  onClick={() => setSelectedQuestion(selectedQuestion === question.id ? null : question.id)}
                >
                  <div className="question-card-header">
                    <div className="question-number">
                      <span className="question-index">#{index + 1}</span>
                      <span className="question-type-badge">{getQuestionType(question)}</span>
                    </div>
                    <div className="question-actions-header">
                      {editingQuestion === question.id ? (
                        <>
                          <button
                            className="question-save-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveEdit(question.id);
                            }}
                            disabled={updatingId === question.id}
                            title="Save changes"
                          >
                            {updatingId === question.id ? (
                              <div className="delete-spinner"></div>
                            ) : (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                <span>Save</span>
                              </>
                            )}
                          </button>
                          <button
                            className="question-cancel-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelEdit();
                            }}
                            disabled={updatingId === question.id}
                            title="Cancel editing"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                            <span>Cancel</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="question-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(question);
                            }}
                            title="Edit question"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            <span>Edit</span>
                          </button>
                          <button
                            className={`question-delete-btn ${showDeleteConfirm === question.id ? 'confirm' : ''} ${deletingId === question.id ? 'deleting' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(question.id);
                            }}
                            disabled={deletingId === question.id}
                            aria-label={showDeleteConfirm === question.id ? 'Confirm delete' : 'Delete question'}
                            title={showDeleteConfirm === question.id ? 'Click again to confirm deletion' : 'Delete this question'}
                          >
                            {deletingId === question.id ? (
                              <div className="delete-spinner"></div>
                            ) : showDeleteConfirm === question.id ? (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                <span className="delete-confirm-text">Confirm</span>
                              </>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {editingQuestion === question.id ? (
                    <div className="question-edit-mode">
                      <div className="question-edit-body">
                        <label>Question:</label>
                        <textarea
                          value={editingData.body}
                          onChange={(e) => setEditingData({ ...editingData, body: e.target.value })}
                          className="question-edit-textarea"
                          placeholder="Enter question text"
                          rows={3}
                        />
                      </div>
                      <div className="question-edit-options">
                        <div className="question-options-label">
                          Options:
                          <button
                            className="add-option-btn-small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddOption();
                            }}
                            type="button"
                          >
                            + Add Option
                          </button>
                        </div>
                        {editingData.options.map((option, optIdx) => (
                          <div key={optIdx} className="question-edit-option">
                            <input
                              type="radio"
                              name={`edit-correct-${question.id}`}
                              checked={option.is_correct}
                              onChange={() => handleUpdateOption(optIdx, 'is_correct', true)}
                              className="option-radio-edit"
                            />
                            <input
                              type="text"
                              value={option.body}
                              onChange={(e) => handleUpdateOption(optIdx, 'body', e.target.value)}
                              className="option-input-edit"
                              placeholder={`Option ${optIdx + 1}`}
                            />
                            {editingData.options.length > 2 && (
                              <button
                                className="delete-option-btn-small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteOption(optIdx);
                                }}
                                type="button"
                                title="Delete option"
                              >
                                ✖
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="question-body">
                        <p className="question-text">{question.body || 'No question text'}</p>
                      </div>

                      {question.options && question.options.length > 0 && (
                        <div className="question-options">
                          <div className="question-options-label">Options:</div>
                          <ul className="question-options-list">
                            {question.options.map((option, optIdx) => (
                              <li
                                key={option.id || optIdx}
                                className={`question-option ${option.is_correct ? 'correct' : ''}`}
                              >
                                <span className="option-letter">{String.fromCharCode(65 + optIdx)}</span>
                                <span className="option-text">{option.body}</span>
                                {option.is_correct && (
                                  <span className="correct-badge">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}

                  {question.test && (
                    <div className="question-meta">
                      <span className="question-test">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                        {question.test.title || 'Untitled Quiz'}
                      </span>
                    </div>
                  )}

                  {selectedQuestion === question.id && (
                    <div className="question-expanded">
                      <div className="question-details">
                        <div className="question-detail-item">
                          <strong>ID:</strong> {question.id}
                        </div>
                        {question.created_at && (
                          <div className="question-detail-item">
                            <strong>Created:</strong> {new Date(question.created_at).toLocaleDateString()}
                          </div>
                        )}
                        {getCorrectAnswer(question) && (
                          <div className="question-detail-item correct-answer">
                            <strong>Correct Answer:</strong> {getCorrectAnswer(question)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="questions-manager-footer">
          <button className="questions-refresh-btn" onClick={fetchQuestions} disabled={loading}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
            Refresh
          </button>
          <div className="questions-footer-info">
            Showing {filteredQuestions.length} of {totalQuestions || questions.length} questions
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionsManager;

